import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { getSelectLanguagesOptions } from '@/shared/dictionaries/languages';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { HighMediumLowList, InformationDisclosureList } from '@/shared/lib/sj-orm/models/asset.dto';
import { PrivateDocumentDto } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';

export enum CategoryType {
  family = 'family',
  money = 'money',
  personal = 'personal',
}

export const CategoryList = [
  {
    label: 'Family',
    key: CategoryType.family,
  },
  {
    label: 'Money',
    key: CategoryType.money,
  },
  {
    label: 'Personal',
    key: CategoryType.personal,
  },
];

export enum CertifiedByType {
  none = 'none',
  notary = 'notary',
  governmentAuthority = 'governmentAuthority',
  consulate = 'consulate',
  signatureWitness = 'signatureWitness',
  other = 'other',
}

export const CertifiedByList = [
  {
    label: 'None',
    key: CertifiedByType.none,
  },
  {
    label: 'Notary',
    key: CertifiedByType.notary,
  },
  {
    label: 'Government authority',
    key: CertifiedByType.governmentAuthority,
  },
  {
    label: 'Signature witness',
    key: CertifiedByType.signatureWitness,
  },
  {
    label: 'Other',
    key: CertifiedByType.other,
  },
];

export type PrivateDocumentDtoType = Omit<
  PrivateDocumentDto,
  'id' | 'type' | 'privateCategory' | 'assetId' | 'beneficiaryId' | 'aiStatus'
>;

export type PrivateDocumentDtoHelpType = {
  name: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  content?: QuestionType.TEXT;
  category?: QuestionType.SELECT_FROM_LIST;
  signatories?: QuestionType.PICK_ARRAY_OF_DTO;
  number?: QuestionType.TEXT;
  startDate?: QuestionType.DATE_TIME;
  stopDate?: QuestionType.DATE_TIME;
  updateRequirementsReason?: QuestionType.TEXT;
  updateRequirementsDate?: QuestionType.DATE_TIME;
  informationDisclosure?: QuestionType.SELECT_FROM_LIST;
  informationDisclosureDetails?: QuestionType.TEXT_MULTILINE;
  languages?: QuestionType.SELECT_FROM_LIST;
  certifiedBy?: QuestionType.SELECT_FROM_LIST;
  notes?: QuestionType.TEXT_MULTILINE;
  attachments?: QuestionType.FILE_UPLOAD;
  storageLocation?: QuestionType.TEXT;
  comments?: QuestionType.TEXT_MULTILINE;
  priority?: QuestionType.SELECT_FROM_LIST;
};

export type PrivateDocumentFormType = TDtoWrapper<
  PrivateDocumentDtoType,
  PrivateDocumentDtoHelpType
>;

export const PrivateDocumentFormSchema = yup.object({
  name: yup.string().required(),
  country: yup.string().required(),
  startDate: yup.date().nullable(),
  stopDate: yup.date().nullable().min(yup.ref('startDate'), 'End date must be after start date'),
});

export const PrivateDocumentForm: PrivateDocumentFormType = {
  name: {
    type: QuestionType.TEXT,
    label: '*Title',
    required: true,
    value: '',
    order: 1,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: '*Country',
    items: CountriesList,
    withSearch: true,
    required: true,
    value: '',
    order: 2,
  },
  content: {
    type: QuestionType.TEXT,
    label: 'Content',
    order: 10,
  },
  category: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'Type',
    items: CategoryList,
    order: 18,
  },
  signatories: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'Signatories',
    mode: 'multiple',
    items: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    order: 9,
  },
  number: {
    type: QuestionType.TEXT,
    label: 'Document number',
    order: 4,
  },
  startDate: {
    type: QuestionType.DATE_TIME,
    label: 'Start date',
    showInFastAdd: true,
    order: 5,
  },
  stopDate: {
    type: QuestionType.DATE_TIME,
    label: 'End date',
    isDateRangePair: true,
    watchFieldName: 'startDate',
    order: 6,
  },
  updateRequirementsReason: {
    type: QuestionType.TEXT,
    label: 'Update requirements reason',
    order: 12,
  },
  updateRequirementsDate: {
    type: QuestionType.DATE_TIME,
    label: 'Update requirements date',
    order: 13,
  },
  informationDisclosure: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'Information disclosure',
    items: InformationDisclosureList,
    order: 14,
  },
  informationDisclosureDetails: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'Information disclosure details',
    order: 15,
  },
  languages: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'Document language(s)',
    items: getSelectLanguagesOptions(),
    mode: 'multiple',
    withSearch: true,
    order: 7,
  },
  certifiedBy: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'Certified by',
    items: CertifiedByList,
    order: 8,
  },
  notes: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'Storage Notes',
    order: 17,
  },
  attachments: {
    type: QuestionType.FILE_UPLOAD,
    label: 'Document scans',
    isMultiple: true,
    order: 3,
  },
  storageLocation: {
    type: QuestionType.TEXT,
    label: 'Storage location',
    order: 16,
  },
  comments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'Comments',
    order: 11,
  },
  priority: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'Priority',
    items: HighMediumLowList,
    order: 19,
  },
};
